import { useEffect, useState } from "react";
import { useQuery, useReactiveVar } from "@apollo/client";

import { authenticationStateVar } from "@/apollo/auth-state";
import { AdminPageShell } from "@/components/admin-page-shell";
import { DraftPreviewPanel } from "@/components/draft-preview-panel";
import { EditorWithPreviewLayout } from "@/components/editor-with-preview-layout";
import { FullPageLoading } from "@/components/full-page-loading";
import { PageIntro } from "@/components/page-intro";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  GET_DRAFT_PREVIEW,
  type DraftPreview,
  type GetDraftPreviewData,
  type GetDraftPreviewVariables,
} from "@/graphql";
import { useDocumentTitle } from "@/hooks/use-document-title";
import type { UseAutoSaveState } from "@/hooks/use-auto-save";
import { useRelativeTime } from "@/hooks/use-relative-time";
import { BuildForm } from "@/pages/build/build-form";

export function BuildPage() {
  useDocumentTitle("Build");
  const { selectedAccount } = useReactiveVar(authenticationStateVar);
  const [autoSaveState, setAutoSaveState] = useState<UseAutoSaveState>({
    isPending: false,
    isSaving: false,
    hasUnsavedChanges: false,
    errorMessage: null,
  });
  const [previewDraft, setPreviewDraft] = useState<DraftPreview | null>(null);

  const { data, loading, error } = useQuery<
    GetDraftPreviewData,
    GetDraftPreviewVariables
  >(GET_DRAFT_PREVIEW, {
    variables: { instagramUsername: selectedAccount },
    skip: !selectedAccount,
  });

  const updatedAtValue = data?.draftPreview?.updatedAt;
  const updatedAt = updatedAtValue
    ? /^[0-9]+$/.test(updatedAtValue)
      ? Number.parseInt(updatedAtValue, 10)
      : Date.parse(updatedAtValue)
    : null;
  const { relative, absolute } = useRelativeTime(updatedAt);

  useEffect(() => {
    setPreviewDraft(data?.draftPreview ?? null);
  }, [data?.draftPreview?.id]);

  if (!selectedAccount) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-neutral-500">
          Select an Instagram account to start building.
        </p>
      </div>
    );
  }

  if (loading) return <FullPageLoading label="Loading your draft…" />;

  if (error || !data?.draftPreview) {
    return (
      <div className="mx-auto max-w-2xl px-8 py-16 text-center">
        <p className="font-inter text-sm text-red-600">
          Couldn&apos;t load your draft. Please refresh and try again.
        </p>
      </div>
    );
  }

  const activePreviewDraft = previewDraft ?? data.draftPreview;

  return (
    <AdminPageShell>
      <div className="space-y-8">
        <PageIntro
          title="Build"
          description="Shape the content, visibility, and order of the elements people see when they open your Sked Link."
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-2 rounded-full border border-neutral-100 bg-white px-3 py-1.5 font-inter text-xs font-medium text-neutral-600">
                <i className="fa-regular fa-clock" />
                Draft saved {relative}
              </span>
            </TooltipTrigger>
            <TooltipContent>{absolute}</TooltipContent>
          </Tooltip>
        </PageIntro>

        <EditorWithPreviewLayout
          editor={
            <BuildForm
              instagramUsername={selectedAccount}
              draftPreview={data.draftPreview}
              onAutoSaveStateChange={setAutoSaveState}
              onPreviewDraftChange={setPreviewDraft}
            />
          }
          preview={
            <DraftPreviewPanel
              draftPreview={activePreviewDraft}
              isSaving={autoSaveState.isSaving}
            />
          }
        />
      </div>
    </AdminPageShell>
  );
}
